import React, {useState, useEffect, useCallback} from 'react';
import {TouchableOpacity, View} from 'react-native';
import EStyleSheet from "react-native-extended-stylesheet";
import {connect} from 'react-redux';

import ContentSection from "../../../../../components/ContentSection";
import {basicStyles} from "../../../../../styles/basic";
import AppText from "../../../../../components/AppText";
import {textStyles} from "../../../../../styles/text";
import AppButton from "../../../../../components/AppButton";
import AppTextInput from "../../../../../components/AppTextInput";
import CloseSvg from "../../../../../components/svgs/CloseSvg";
import {setWarning} from "../../../../../store/warning/warningActions";
import {bindActionCreators} from "redux";
import {formatPhoneNumber} from "../../../../../utilities/methods";
import MessagesService from "../../../../../services/MessagesService";

function TaskPerMessage({organizationId, setWarning}) {
    const [taskPerMessageList, setTaskPerMessageList] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [inputError, setInputError] = useState('');

    // Helper function to transform API response to component format
    const transformSetting = useCallback((setting) => {
        const value = setting.organization_message_feed_setting_value;
        const isEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);
        
        return {
            organization_message_feed_setting_id: setting.organization_message_feed_setting_id,
            organization_message_feed_setting_key: setting.organization_message_feed_setting_key,
            organization_message_feed_setting_value: value,
            type: isEmail ? 'email' : 'phone',
            displayValue: isEmail ? value : (formatPhoneNumber(value) || value)
        };
    }, []);

    const validateInput = useCallback((value) => {
        if (!value || value.trim() === '') {
            setInputError('Please enter a phone number or email address');
            return false;
        }

        const trimmedValue = value.trim();
        
        // Check if it's an email
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (emailRegex.test(trimmedValue)) {
            setInputError('');
            return true;
        }

        // Check if it's a phone number (digits only, at least 10 digits)
        const phoneRegex = /^[\d\s\-()+]+$/;
        const digitsOnly = trimmedValue.replace(/\D/g, '');
        if (phoneRegex.test(trimmedValue) && digitsOnly.length >= 10) {
            setInputError('');
            return true;
        }

        setInputError('Please enter a valid phone number or email address');
        return false;
    }, []);

    const handleSave = useCallback(async () => {
        if (!validateInput(inputValue)) {
            return;
        }

        const trimmedValue = inputValue.trim();
        const isEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(trimmedValue);
        
        // Normalize phone numbers
        const normalizedValue = isEmail 
            ? trimmedValue.toLowerCase() 
            : trimmedValue.replace(/\D/g, ''); // Strip all non-digits for phone numbers
        
        // Check if already exists (compare normalized values)
        if (taskPerMessageList.some(item => {
            const itemValue = item.organization_message_feed_setting_value;
            const itemNormalized = item.type === 'email' 
                ? itemValue.toLowerCase() 
                : itemValue.replace(/\D/g, '');
            return itemNormalized === normalizedValue;
        })) {
            setInputError('This phone number or email is already in the list');
            return;
        }

        let response = await MessagesService.insertMessageSetting(organizationId, 'task-per-message', normalizedValue);

        const transformedSetting = transformSetting(response.setting);
        setTaskPerMessageList(current => [...current, transformedSetting]);
        setInputValue('');
        setInputError('');
    }, [inputValue, taskPerMessageList, validateInput, organizationId, transformSetting]);

    const handleRemove = useCallback((settingId) => {
        setWarning({
            confirmAction: async () => {
                // TODO: Add deleteMessageSetting method to MessagesService when available
                await MessagesService.deleteMessageSetting(organizationId, settingId);
                setTaskPerMessageList(current => current.filter(item => 
                    item.organization_message_feed_setting_id !== settingId
                ));
            },
            warningMessage: 'Are you sure you want to remove this phone number or email?',
            confirmLabel: 'Confirm',
            rejectLabel: 'Cancel',
            active: true
        });
    }, [setWarning, organizationId]);

    useEffect(() => {
        if (organizationId) {
            MessagesService.getMessageSetting(organizationId, 'task-per-message')
                .then(response => {
                    // Transform each setting to component format
                    const transformedSettings = response.settings.map(transformSetting);
                    setTaskPerMessageList(transformedSettings);
                })
                .catch(error => {
                    console.log('task per message error', error);
                });
        }
    }, [organizationId, transformSetting]);

    return (
        <>
            <View
                style={[basicStyles.flexRow, basicStyles.justifyContentSpaceBetween, basicStyles.alignContentCenter, {marginBottom: 25}]}>
                <AppText style={[textStyles.pageSubTitle, textStyles.semiBold, {marginBottom: 0}]}>
                    Task Per Message
                </AppText>
            </View>

            <ContentSection style={[
                basicStyles.inputContainerPaddingVertical,
                basicStyles.inputContainerPaddingHorizontal,
                {marginBottom: 40}
            ]}>
                <ContentSection style={[
                    basicStyles.inputContainerPaddingHorizontal,
                    styles.itemContainer,
                ]}>
                    <View style={[basicStyles.flexRow, basicStyles.alignContentCenter]}>
                        <AppTextInput
                            // label="Phone Number or Email Address"
                            value={inputValue}
                            onChangeText={(value) => {
                                setInputValue(value);
                                if (inputError) {
                                    setInputError('');
                                }
                            }}
                            placeholder="Enter phone number or email"
                            wrapperStyle={{height: 44}}
                            inputStyle={{paddingRight: 12}}
                        />
                        {inputError ? (
                            <AppText style={styles.errorText}>
                                {inputError}
                            </AppText>
                        ) : null}
                    </View>
                    <View style={{marginVertical: 8}}>
                        <AppButton
                            label="Save"
                            action={handleSave}
                        />
                    </View>
                </ContentSection>

                {taskPerMessageList.length > 0 ? (
                    taskPerMessageList.map((item) => {
                        return (
                            <ContentSection key={item.organization_message_feed_setting_id} style={[
                                // basicStyles.inputContainerPaddingVertical,
                                basicStyles.inputContainerPaddingHorizontal,
                                styles.itemContainer,
                            ]}>
                                <View style={[basicStyles.flexRow, basicStyles.alignContentCenter]}>
                                    <AppText style={styles.itemValue}>
                                        {item.displayValue || item.organization_message_feed_setting_value}
                                    </AppText>
                                </View>

                                <AppButton
                                    label="Delete"
                                    action={() => {
                                        handleRemove(item.organization_message_feed_setting_id);
                                    }}
                                    theme="transRed"
                                />

                            </ContentSection>
                        );
                    })
                ) : (
                    <ContentSection style={[
                        basicStyles.inputContainerPaddingVertical,
                        basicStyles.inputContainerPaddingHorizontal,
                        basicStyles.alignContentCenter,
                        basicStyles.justifyContentCenter,
                    ]}>
                        <AppText>
                            No phone numbers or email addresses configured
                        </AppText>
                    </ContentSection>
                )}

            </ContentSection>
        </>
    );
}

const mapDispatchToProps = (dispatch) => (
    bindActionCreators({
        setWarning,
    }, dispatch)
);

export default connect(null, mapDispatchToProps)(TaskPerMessage);

const styles = EStyleSheet.create({
    itemContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 14,
        paddingVertical: 8,
    },
    itemValue: {
        fontFamily: 'SourceSansPro-Regular',
        fontSize: 16,
        color: '#000000',
        flex: 1,
        paddingLeft: 12,
    },
    errorText: {
        color: '#EA0024',
        fontSize: 12,
        marginTop: 5,
        fontFamily: 'SourceSansPro-Regular',
        paddingLeft: 12,
    }
});

